import React from 'react';
import { motion } from 'framer-motion';
import { Users, ArrowLeft } from 'lucide-react';
import { AudioManager } from '../../lib/AudioManager';
import { CONSTANTS } from '../../data';

const PlayerSelectionModal = ({
  onSelect,
  onBack,
  audioManager,
}: {
  onSelect: (numPlayers: number) => void;
  onBack: () => void;
  audioManager: AudioManager;
}) => {
  const handleSelect = (num: number) => {
    audioManager.playSound('buttonClick');
    onSelect(num);
  };

  const handleBack = () => {
    audioManager.playSound('buttonClick');
    onBack();
  };

  return (
    <motion.div
      className="fixed inset-0 z-[4500] flex flex-col items-center justify-center backdrop-blur-md"
      style={{ background: 'var(--color-primary)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="flex flex-col items-center gap-8 p-10 rounded-3xl shadow-2xl border-2"
        style={{
          background: `linear-gradient(135deg, var(--color-primary-dark), var(--color-primary))`,
          borderColor: 'var(--color-accent)'
        }}
        initial={{ scale: 0.8, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <motion.h1
          className="text-4xl md:text-5xl font-extrabold drop-shadow-lg tracking-wide text-center"
          style={{ color: 'var(--color-accent)' }}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 120, damping: 10 }}
        >
          {CONSTANTS.UI_TEXT.SELECT_PLAYERS_TITLE}
        </motion.h1>
        
        <motion.div 
          className="mt-4 flex flex-col gap-4 w-full max-w-xs"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {[2, 3, 4].map(num => (
            <motion.button
              key={num}
              onClick={() => handleSelect(num)}
              className="w-full px-10 py-4 rounded-xl font-extrabold text-xl shadow-xl transition-all border-2 hover:scale-105 active:scale-95 flex items-center justify-center gap-3"
              style={{
                background: 'var(--color-button)',
                color: 'var(--color-button-text)',
                borderColor: 'var(--color-accent)'
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Users size={24} />
              <span>{num} {CONSTANTS.UI_TEXT.PLAYERS_LABEL}</span>
            </motion.button>
          ))}
        </motion.div>

        <motion.button
          onClick={handleBack}
          className="mt-4 flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-base transition-all duration-300 shadow-lg border-2 hover:scale-105 active:scale-95"
          style={{
            background: `linear-gradient(to right, var(--color-primary-dark), var(--color-primary))`,
            color: 'var(--color-text)',
            borderColor: 'var(--color-accent)'
          }}
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <ArrowLeft size={20} />
          <span>{CONSTANTS.UI_TEXT.BACK_BUTTON}</span>
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default PlayerSelectionModal;
